/**
 * Main WebGL2 renderer.
 *
 * Handles:
 * - WebGL2 context creation and management
 * - Shader program management
 * - Batch rendering of 2D shapes (triangles, rectangles, lines)
 * - Post-processing pipeline (bloom, compose)
 * - Canvas resize handling
 */

import { createProgram, createBuffer, createFramebuffer, getAttribLocation, getUniformLocation } from './gl-utils';
import { Camera2D } from './camera';
import { Camera3D } from './camera3d';
import type { Color } from '@/types';

// Import shader sources as strings (Vite handles this with ?raw)
import basicVertSrc from './shaders/basic.vert.glsl?raw';
import basicFragSrc from './shaders/basic.frag.glsl?raw';
import glowVertSrc from './shaders/glow.vert.glsl?raw';
import glowFragSrc from './shaders/glow.frag.glsl?raw';
import composeFragSrc from './shaders/compose.frag.glsl?raw';
import gridVertSrc from './shaders/grid.vert.glsl?raw';
import gridFragSrc from './shaders/grid.frag.glsl?raw';
import basic3dVertSrc from './shaders/basic3d.vert.glsl?raw';
import basic3dFragSrc from './shaders/basic3d.frag.glsl?raw';

/** Maximum vertices per batch draw call */
const MAX_BATCH_VERTICES = 4096;
/** Floats per vertex: x, y, r, g, b, a */
const VERTEX_SIZE = 6;

export class Renderer {
  readonly gl: WebGL2RenderingContext;
  readonly canvas: HTMLCanvasElement;
  readonly camera: Camera2D;

  // Shader programs
  private basicProgram!: WebGLProgram;
  private glowProgram!: WebGLProgram;
  private composeProgram!: WebGLProgram;
  private gridProgram!: WebGLProgram;
  private basic3dProgram!: WebGLProgram;

  // 2D Batch rendering
  private batchBuffer!: WebGLBuffer;
  private batchData = new Float32Array(MAX_BATCH_VERTICES * VERTEX_SIZE);
  private batchVertexCount = 0;

  // 3D Batch rendering (x, y, z, r, g, b, a = 7 floats per vertex)
  private batch3dBuffer!: WebGLBuffer;
  private batch3dData = new Float32Array(MAX_BATCH_VERTICES * 7);
  private batch3dVertexCount = 0;

  // Post-processing framebuffers
  private sceneFB!: { framebuffer: WebGLFramebuffer; texture: WebGLTexture };
  private bloomFB1!: { framebuffer: WebGLFramebuffer; texture: WebGLTexture };
  private bloomFB2!: { framebuffer: WebGLFramebuffer; texture: WebGLTexture };
  private quadBuffer!: WebGLBuffer;

  // State
  width = 0;
  height = 0;
  bloomStrength = 0.6;
  vignetteIntensity = 0.3;
  beatPulse = 0;
  chromaticAberration = 0;

  // 3D camera
  readonly camera3d: Camera3D;

  private onResize: () => void;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.camera = new Camera2D();
    this.camera3d = new Camera3D();

    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      premultipliedAlpha: false,
    });
    if (!gl) throw new Error('WebGL2 not supported');
    this.gl = gl;

    this.initShaders();
    this.initBuffers();
    this.resize();

    this.onResize = () => this.resize();
    window.addEventListener('resize', this.onResize);
  }

  /** Remove window listeners. Safe to call when the canvas will be reused. */
  detachListeners(): void {
    window.removeEventListener('resize', this.onResize);
  }

  /**
   * Release the WebGL context and remove listeners. Call this when the
   * renderer's canvas is about to be discarded — without `loseContext()`
   * the browser keeps the context alive until GC, which exhausts the
   * per-process WebGL context pool under rapid create/destroy cycles.
   */
  dispose(): void {
    this.detachListeners();
    this.gl.getExtension('WEBGL_lose_context')?.loseContext();
  }

  private initShaders(): void {
    const gl = this.gl;
    this.basicProgram = createProgram(gl, basicVertSrc, basicFragSrc);
    this.glowProgram = createProgram(gl, glowVertSrc, glowFragSrc);
    this.composeProgram = createProgram(gl, glowVertSrc, composeFragSrc);
    this.gridProgram = createProgram(gl, gridVertSrc, gridFragSrc);
    this.basic3dProgram = createProgram(gl, basic3dVertSrc, basic3dFragSrc);
  }

  private initBuffers(): void {
    const gl = this.gl;
    this.batchBuffer = createBuffer(gl);
    this.batch3dBuffer = createBuffer(gl);

    // Full-screen quad for post-processing (position + texcoord)
    this.quadBuffer = createBuffer(
      gl,
      new Float32Array([
        -1, -1, 0, 0,
         1, -1, 1, 0,
        -1,  1, 0, 1,
         1,  1, 1, 1,
      ]),
    );
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio, 2);
    const displayWidth = Math.floor(this.canvas.clientWidth * dpr);
    const displayHeight = Math.floor(this.canvas.clientHeight * dpr);

    const sizeChanged = this.canvas.width !== displayWidth || this.canvas.height !== displayHeight;
    const needsInit = !this.sceneFB;

    if (sizeChanged || needsInit) {
      this.canvas.width = displayWidth;
      this.canvas.height = displayHeight;
      this.width = displayWidth;
      this.height = displayHeight;
      this.gl.viewport(0, 0, displayWidth, displayHeight);

      // Recreate framebuffers at new size
      const gl = this.gl;
      const w = Math.floor(displayWidth / 2); // Bloom at half resolution
      const h = Math.floor(displayHeight / 2);
      this.sceneFB = createFramebuffer(gl, displayWidth, displayHeight, true);
      this.bloomFB1 = createFramebuffer(gl, w, h);
      this.bloomFB2 = createFramebuffer(gl, w, h);

      this.camera.resize(displayWidth, displayHeight);
      this.camera3d.resize(displayWidth, displayHeight);
    }
  }

  /** Begin a new frame — clears and binds the scene framebuffer */
  beginFrame(clearColor?: Color): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.sceneFB.framebuffer);
    gl.viewport(0, 0, this.width, this.height);
    if (clearColor) {
      gl.clearColor(clearColor.r, clearColor.g, clearColor.b, clearColor.a);
    } else {
      gl.clearColor(0.04, 0.04, 0.1, 1.0); // Default dark blue-black
    }
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** End the frame — runs post-processing and presents to screen */
  endFrame(): void {
    this.flushBatch();
    this.postProcess();
  }

  // ─── Batch Rendering ──────────────────────────────────────

  /** Push a triangle into the batch */
  drawTriangle(
    x1: number, y1: number,
    x2: number, y2: number,
    x3: number, y3: number,
    color: Color,
  ): void {
    if (this.batchVertexCount + 3 > MAX_BATCH_VERTICES) this.flushBatch();
    const d = this.batchData;
    let i = this.batchVertexCount * VERTEX_SIZE;
    d[i++] = x1; d[i++] = y1; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    d[i++] = x2; d[i++] = y2; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    d[i++] = x3; d[i++] = y3; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    this.batchVertexCount += 3;
  }

  /** Push a rectangle into the batch (as two triangles) */
  drawRect(x: number, y: number, w: number, h: number, color: Color): void {
    if (this.batchVertexCount + 6 > MAX_BATCH_VERTICES) this.flushBatch();
    const d = this.batchData;
    let i = this.batchVertexCount * VERTEX_SIZE;
    const { r, g, b, a } = color;
    // Triangle 1
    d[i++] = x;     d[i++] = y;     d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    d[i++] = x + w; d[i++] = y;     d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    d[i++] = x;     d[i++] = y + h; d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    // Triangle 2
    d[i++] = x + w; d[i++] = y;     d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    d[i++] = x + w; d[i++] = y + h; d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    d[i++] = x;     d[i++] = y + h; d[i++] = r; d[i++] = g; d[i++] = b; d[i++] = a;
    this.batchVertexCount += 6;
  }

  /** Push a line (as a thin quad) into the batch */
  drawLine(
    x1: number, y1: number,
    x2: number, y2: number,
    thickness: number,
    color: Color,
  ): void {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 0.001) return;
    const nx = (-dy / len) * thickness * 0.5;
    const ny = (dx / len) * thickness * 0.5;

    this.drawTriangle(
      x1 + nx, y1 + ny,
      x1 - nx, y1 - ny,
      x2 + nx, y2 + ny,
      color,
    );
    this.drawTriangle(
      x1 - nx, y1 - ny,
      x2 - nx, y2 - ny,
      x2 + nx, y2 + ny,
      color,
    );
  }

  /** Flush the current batch to the GPU */
  flushBatch(): void {
    if (this.batchVertexCount === 0) return;
    const gl = this.gl;

    gl.useProgram(this.basicProgram);

    // Upload batch data
    gl.bindBuffer(gl.ARRAY_BUFFER, this.batchBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.batchData.subarray(0, this.batchVertexCount * VERTEX_SIZE),
      gl.DYNAMIC_DRAW,
    );

    // Set up vertex attributes
    const stride = VERTEX_SIZE * 4; // bytes
    const posLoc = getAttribLocation(gl, this.basicProgram, 'a_position');
    const colLoc = getAttribLocation(gl, this.basicProgram, 'a_color');

    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(colLoc);
    gl.vertexAttribPointer(colLoc, 4, gl.FLOAT, false, stride, 8);

    // Set view-projection matrix
    const vpLoc = getUniformLocation(gl, this.basicProgram, 'u_viewProjection');
    gl.uniformMatrix3fv(vpLoc, false, this.camera.getViewProjectionMatrix());

    gl.drawArrays(gl.TRIANGLES, 0, this.batchVertexCount);
    this.batchVertexCount = 0;
  }

  // ─── Grid Rendering ───────────────────────────────────────

  /** Draw the Tron-style grid floor */
  drawGrid(baseY: number, color: Color, spacing = 50, intensity = 0.5): void {
    this.flushBatch(); // Grid uses its own shader

    const gl = this.gl;
    gl.useProgram(this.gridProgram);

    // Build a large quad covering the visible area
    const cam = this.camera;
    const left = cam.position.x - cam.viewWidth;
    const right = cam.position.x + cam.viewWidth;
    const bottom = baseY - 200;
    const top = baseY + 10;

    const gridVerts = new Float32Array([
      left, bottom, right, bottom, left, top,
      right, bottom, right, top, left, top,
    ]);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.batchBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, gridVerts, gl.DYNAMIC_DRAW);

    const posLoc = getAttribLocation(gl, this.gridProgram, 'a_position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.uniformMatrix3fv(
      getUniformLocation(gl, this.gridProgram, 'u_viewProjection'),
      false,
      cam.getViewProjectionMatrix(),
    );
    gl.uniform4f(
      getUniformLocation(gl, this.gridProgram, 'u_color'),
      color.r, color.g, color.b, color.a,
    );
    gl.uniform1f(getUniformLocation(gl, this.gridProgram, 'u_gridSpacing'), spacing);
    gl.uniform1f(getUniformLocation(gl, this.gridProgram, 'u_lineWidth'), 1.5);
    gl.uniform1f(getUniformLocation(gl, this.gridProgram, 'u_intensity'), intensity);
    gl.uniform1f(getUniformLocation(gl, this.gridProgram, 'u_fadeStart'), 0);
    gl.uniform1f(getUniformLocation(gl, this.gridProgram, 'u_fadeEnd'), 300);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // ─── 3D Rendering ─────────────────────────────────────────

  /** Push a 3D triangle into the 3D batch */
  drawTriangle3d(
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    x3: number, y3: number, z3: number,
    color: Color,
  ): void {
    if (this.batch3dVertexCount + 3 > MAX_BATCH_VERTICES) this.flush3dBatch();
    const d = this.batch3dData;
    let i = this.batch3dVertexCount * 7;
    d[i++] = x1; d[i++] = y1; d[i++] = z1; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    d[i++] = x2; d[i++] = y2; d[i++] = z2; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    d[i++] = x3; d[i++] = y3; d[i++] = z3; d[i++] = color.r; d[i++] = color.g; d[i++] = color.b; d[i++] = color.a;
    this.batch3dVertexCount += 3;
  }

  /** Draw a 3D line (as a thin quad that faces the camera) */
  drawLine3d(
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    thickness: number,
    color: Color,
  ): void {
    // Make the line face the camera by expanding perpendicular to both
    // the line direction and the view direction (approximate: use Y-up)
    const dx = x2 - x1;
    const dy = y2 - y1;
    const dz = z2 - z1;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 0.001) return;

    // Cross product of line direction with camera forward (0,0,-1)
    // gives a vector perpendicular to the line in screen space
    let nx = -dy;
    let ny = dx;
    let nz = 0;
    const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (nLen < 0.001) {
      // Line is along Z — use X-up instead
      nx = 0; ny = thickness * 0.5; nz = 0;
    } else {
      const s = (thickness * 0.5) / nLen;
      nx *= s; ny *= s; nz *= s;
    }

    this.drawTriangle3d(
      x1 + nx, y1 + ny, z1 + nz,
      x1 - nx, y1 - ny, z1 - nz,
      x2 + nx, y2 + ny, z2 + nz,
      color,
    );
    this.drawTriangle3d(
      x1 - nx, y1 - ny, z1 - nz,
      x2 - nx, y2 - ny, z2 - nz,
      x2 + nx, y2 + ny, z2 + nz,
      color,
    );
  }

  /** Flush the 3D batch to the GPU. fogNear/fogFar are in world units (view distance). */
  flush3dBatch(fogNear = 200, fogFar = 550, fogColor = [0.04, 0.04, 0.1]): void {
    if (this.batch3dVertexCount === 0) return;
    const gl = this.gl;

    gl.useProgram(this.basic3dProgram);
    gl.enable(gl.DEPTH_TEST);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.batch3dBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.batch3dData.subarray(0, this.batch3dVertexCount * 7),
      gl.DYNAMIC_DRAW,
    );

    const stride = 7 * 4; // bytes
    const posLoc = getAttribLocation(gl, this.basic3dProgram, 'a_position');
    const colLoc = getAttribLocation(gl, this.basic3dProgram, 'a_color');

    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(colLoc);
    gl.vertexAttribPointer(colLoc, 4, gl.FLOAT, false, stride, 12);

    // Set MVP
    const mvp = this.camera3d.getViewProjectionMatrix();
    const mvpLoc = getUniformLocation(gl, this.basic3dProgram, 'u_mvp');
    gl.uniformMatrix4fv(mvpLoc, false, mvp);

    // Fog uniforms
    gl.uniform1f(getUniformLocation(gl, this.basic3dProgram, 'u_fogNear'), fogNear);
    gl.uniform1f(getUniformLocation(gl, this.basic3dProgram, 'u_fogFar'), fogFar);
    gl.uniform3f(getUniformLocation(gl, this.basic3dProgram, 'u_fogColor'), fogColor[0], fogColor[1], fogColor[2]);

    gl.drawArrays(gl.TRIANGLES, 0, this.batch3dVertexCount);
    this.batch3dVertexCount = 0;

    gl.disable(gl.DEPTH_TEST);
  }

  // ─── Post-Processing ──────────────────────────────────────

  private postProcess(): void {
    const gl = this.gl;
    const bw = Math.floor(this.width / 2);
    const bh = Math.floor(this.height / 2);

    // Pass 1: Extract bright pixels + horizontal blur into bloomFB1
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.bloomFB1.framebuffer);
    gl.viewport(0, 0, bw, bh);
    gl.useProgram(this.glowProgram);
    this.bindQuad(this.glowProgram);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.sceneFB.texture);
    gl.uniform1i(getUniformLocation(gl, this.glowProgram, 'u_texture'), 0);
    gl.uniform2f(getUniformLocation(gl, this.glowProgram, 'u_resolution'), bw, bh);
    gl.uniform2f(getUniformLocation(gl, this.glowProgram, 'u_direction'), 1.0, 0.0);
    gl.uniform1f(getUniformLocation(gl, this.glowProgram, 'u_intensity'), 1.0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Pass 2: Vertical blur into bloomFB2
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.bloomFB2.framebuffer);
    gl.bindTexture(gl.TEXTURE_2D, this.bloomFB1.texture);
    gl.uniform2f(getUniformLocation(gl, this.glowProgram, 'u_direction'), 0.0, 1.0);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Pass 3: Compose scene + bloom to screen
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.useProgram(this.composeProgram);
    this.bindQuad(this.composeProgram);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.sceneFB.texture);
    gl.uniform1i(getUniformLocation(gl, this.composeProgram, 'u_scene'), 0);

    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.bloomFB2.texture);
    gl.uniform1i(getUniformLocation(gl, this.composeProgram, 'u_bloom'), 1);

    gl.uniform1f(getUniformLocation(gl, this.composeProgram, 'u_bloomStrength'), this.bloomStrength);
    gl.uniform1f(getUniformLocation(gl, this.composeProgram, 'u_vignetteIntensity'), this.vignetteIntensity);
    gl.uniform1f(getUniformLocation(gl, this.composeProgram, 'u_beatPulse'), this.beatPulse);
    gl.uniform1f(getUniformLocation(gl, this.composeProgram, 'u_chromaticAberration'), this.chromaticAberration);

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Decay beat pulse and chromatic aberration
    this.beatPulse *= 0.85;
    this.chromaticAberration *= 0.92;
  }

  private bindQuad(program: WebGLProgram): void {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);

    const posLoc = getAttribLocation(gl, program, 'a_position');
    const uvLoc = getAttribLocation(gl, program, 'a_texCoord');

    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 16, 0);
    if (uvLoc !== -1) {
      gl.enableVertexAttribArray(uvLoc);
      gl.vertexAttribPointer(uvLoc, 2, gl.FLOAT, false, 16, 8);
    }
  }
}
